import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteIcon from "@mui/icons-material/Delete";
import { Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  useAllApplicationsListQuery,
  useDeleteApplicationMutation,
  useExportAllApplicationsMutation,
} from "api/applications/applications.mutation";
import applicationCountIcon from "assets/admin/applications_count.svg";
import excelIcon from "assets/admin/excel.svg";
import filterIcon from "assets/admin/filter.svg";
import { ConfirmDeleteModal } from "components/ConfirmDeleteModal";
import { Button } from "components/LoadingButton";
import SwitchField from "components/SwitchField";
import { Table } from "components/Table";
import { handleError } from "functions/apiHandleError";
import { downloadBlob } from "functions/downloadBlob";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import useStore from "store";
import { ApplicationFilterModal } from "./components/filterModal";
import { ApplicationReferralModal } from "./components/referralModal";
import classes from "./style.module.scss";

const defaultFormFields = {
  age_min: 0,
  age_max: 80,
  university_status: "",
  faculty_status: "",
  country: "",
  degree: "",
  faculty: "",
  field_of_study: "",
  group: "",
  agent: "",
  email: "",
  gender: "",
  create_at_after: "",
  create_at_before: "",
  search: "",
  more_than_one_request: false,
  archived: false,
  show_incomplete: false,
};

const ApplicationsList = () => {
  const { userInfoStore } = useStore();
  const [searchParams] = useSearchParams();
  const paramPage = Number(searchParams.get("page")) || 1;

  const { reset, getValues, resetField, watch, control, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      age_min: 0,
      age_max: 80,
      status: "",
      faculty_status: "",
      country: "",
      degree: "",
      faculty: "",
      field_of_study: "",
      group: "",
      agent: "",
      email: "",
      gender: "",
      create_at_after: "",
      create_at_before: "",
      search: "",
      more_than_one_request: false,
      archived: false,
      show_incomplete: false,
    },
  });

  const archived = watch("archived");
  const showIncomplete = watch("show_incomplete");

  const [reload, setReload] = useState(false);

  const [pageFilter, setPageFilter] = useState({
    page: paramPage,
    page_size: 15,
    total: 1,
    count: 0,
    sort: "-create_at",
  });

  const [filterModal, setFilterModal] = useState({
    open: false,
  });

  const [referralModal, setReferralModal] = useState({
    open: false,
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, selectedApplication: null });

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedStaffs, setSellectedStaffs] = useState([]);

  const createFilters = () => {
    const { show_incomplete, ...formFilters } = getValues();

    return {
      ...formFilters,
      archived,
      is_completed: !showIncomplete,
    };
  };

  const filters = createFilters();

  const applicationsQuery = useAllApplicationsListQuery({
    page: pageFilter.page,
    page_size: pageFilter.page_size,
    ordering: pageFilter.sort,
    filters,
    reload,
  });

  const deleteApplicationMutation = useDeleteApplicationMutation();
  const exportAllApplicationsMutation = useExportAllApplicationsMutation();

  const tableData = useMemo(
    () =>
      (applicationsQuery.data?.results ?? []).map((row, index) => ({
        ...row,
        index: (pageFilter.page - 1) * pageFilter.page_size + index + 1,
      })),
    [applicationsQuery.data, pageFilter.page, pageFilter.page_size]
  );

  const totalPages = applicationsQuery.data?.total ?? pageFilter.total;
  const applicationsCount = applicationsQuery.data?.count_all ?? pageFilter.count;
  const tableLoading = applicationsQuery.isFetching || deleteApplicationMutation.isPending;

  const handleDeleteApplication = async () => {
    const applicationID = deleteModal.selectedApplication?.id;
    if (!applicationID) return;

    try {
      await deleteApplicationMutation.mutateAsync(applicationID);
      setDeleteModal({ open: false, selectedApplication: null });
    } catch (err) {
      handleError({ err });
    }
  };

  const handleCloseFilterModal = () => {
    setFilterModal({ open: false });
  };

  const handleSelectRow = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const getExportedData = async () => {
    try {
      const filters = createFilters();

      const response = await exportAllApplicationsMutation.mutateAsync({
        ordering: pageFilter.sort,
        filters,
      });

      downloadBlob(response, "export_orders.xlsx");
    } catch (error) {}
  };

  const handleOpenReferralModal = () => {
    if (selectedRows.length === 0) {
      toast.warning("لطفا حداقل یک پرونده را انتخاب کنید");
      return;
    }

    const items = tableData.filter((row) => selectedRows.includes(row.id));

    const hasInvalid = items.some((row) => !row?.can_referral);

    if (hasInvalid) {
      items.forEach((row) => {
        if (!row?.can_referral) {
          toast.warning(`شما اجازه ارجاع پرونده ${row?.tracking_id} را ندارید.`);
        }
      });

      return;
    }

    const firstGroup = items[0].group;
    const allSameGroup = items.every((item) => item.group === firstGroup);

    if (!allSameGroup) {
      toast.warning("تمام پرونده‌های انتخابی باید در یک گروه باشند.");
      return;
    }

    setSellectedStaffs(items?.[0]?.staffs);
    setReferralModal({ open: true });
  };

  useEffect(() => {
    if (pageFilter.page !== paramPage) {
      setPageFilter((prev) => ({
        ...prev,
        page: paramPage,
      }));
    }
  }, [paramPage]);

  useEffect(() => {
    let valueChanged = false;

    for (const [key, value] of searchParams.entries()) {
      if (key in defaultFormFields) {
        let typedValue = value;

        if (typeof defaultFormFields[key] === "number") {
          typedValue = Number(value);
        } else if (typeof defaultFormFields[key] === "boolean") {
          typedValue = value === "true";
        }

        valueChanged = true;
        setValue(key, typedValue);
      }
    }

    if (valueChanged) {
      setReload((prev) => !prev);
    }
  }, [searchParams, setValue]);

  useEffect(() => {
    setPageFilter((prev) => ({
      ...prev,
      page: 1,
    }));

    setReload((prev) => !prev);
  }, [showIncomplete]);

  const columns = [
    {
      field: "index",
      headerName: "ردیف",
      minWidth: 60,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "tracking_id",
      headerName: "شماره پرونده",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "full_name",
      headerName: "نام دانشجو",
      renderCell: ({ row }) => row?.user?.full_name,
      minWidth: 120,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "passport_number",
      headerName: "شماره پاسپورت",
      renderCell: ({ row }) => row?.user?.passport_number,
      minWidth: 120,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "gender",
      headerName: "جنسیت",
      minWidth: 60,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.user?.gender,
    },
    {
      field: "age",
      headerName: "سن",
      minWidth: 50,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.user?.age?.toLocaleString("fa-ir"),
    },
    {
      field: "country",
      headerName: "کشور",
      minWidth: 110,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.user?.country,
    },
    {
      field: "gpa",
      headerName: "نمره",
      minWidth: 110,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => (
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column" }}>
          <Typography variant="caption">{`کارشناسی: ${row?.bachelor_gpa}`}</Typography>
          <Typography variant="caption">{`ارشد: ${row?.master_gpa}`}</Typography>
        </div>
      ),
    },
    {
      field: "degree",
      headerName: "مقطع",
      minWidth: 60,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "faculty",
      headerName: "رشته",
      minWidth: 80,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "field_of_study",
      headerName: "گرایش",
      minWidth: 130,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "group",
      headerName: "گروه",
      minWidth: 110,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "jalali_created_at",
      headerName: "تاریخ درخواست",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => (
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column" }}>
          <Typography variant="caption">{row?.jalali_created_at?.split(" - ")[0]}</Typography>
          <Typography variant="caption">{row?.jalali_created_at?.split(" - ")[1]}</Typography>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "وضعیت",
      minWidth: 80,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.status,
    },
    {
      field: "action",
      headerName: "",
      minWidth: 80,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => (
        <>
          {userInfoStore.is_superuser && (
            <Tooltip title="حذف پرونده">
              <IconButton
                color="error"
                onClick={() => setDeleteModal({ open: true, selectedApplication: row })}
              >
                <DeleteIcon className={classes.nextIcon} fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Link
            className={classes.nextBox}
            to={`/dashboard/all-files/detail/${row?.id}`}
            target="_blank"
          >
            <ArrowBackIosNewIcon className={classes.nextIcon} fontSize="small" />
          </Link>
        </>
      ),
    },
  ];

  return (
    <>
      <div className={classes.mainDiv}>
        <div className={classes.headerTable}>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
            {userInfoStore?.is_superuser && (
              <div className={classes.itemBox}>
                <Button size="2xsmall" color="secondary" onClick={handleOpenReferralModal}>
                  ارجاع گروهی
                </Button>
              </div>
            )}

            <div className={classes.itemBox}>
              <SwitchField
                name="archived"
                control={control}
                label="پرونده های بایگانی"
                size="small"
              />
            </div>

            <div className={classes.itemBox}>
              <SwitchField
                name="show_incomplete"
                control={control}
                label="پرونده‌های تکمیل نشده"
                size="small"
              />
            </div>

            <div className={classes.itemBox}>
              <Typography variant="subtitle2">فیتلر</Typography>
              <div
                className={classes.filterIconBox}
                onClick={() =>
                  setFilterModal({
                    open: true,
                  })
                }
              >
                <LazyLoadImage className={classes.filterIcon} src={filterIcon} />
              </div>
            </div>

            <div className={classes.itemBox}>
              <Typography variant="subtitle2">نمایش بر اساس:</Typography>

              <div className={classes.methods}>
                <Typography
                  variant="caption"
                  className={`${classes.sortItemText} ${
                    pageFilter.sort === "-bachelor_gpa" && classes.sortItemTextActive
                  }`}
                  onClick={() => setPageFilter({ ...pageFilter, sort: "-bachelor_gpa" })}
                >
                  بیشترین نمره کارشناسی
                </Typography>

                <Typography
                  variant="caption"
                  className={`${classes.sortItemText} ${
                    pageFilter.sort === "-master_gpa" && classes.sortItemTextActive
                  }`}
                  onClick={() => setPageFilter({ ...pageFilter, sort: "-master_gpa" })}
                >
                  بیشترین نمره ارشد
                </Typography>

                <Typography
                  variant="caption"
                  className={`${classes.sortItemText} ${
                    pageFilter.sort === "-create_at" && classes.sortItemTextActive
                  }`}
                  onClick={() => setPageFilter({ ...pageFilter, sort: "-create_at" })}
                >
                  جدیدترین
                </Typography>

                <Typography
                  variant="caption"
                  className={`${classes.sortItemText} ${
                    pageFilter.sort === "create_at" && classes.sortItemTextActive
                  }`}
                  onClick={() => setPageFilter({ ...pageFilter, sort: "create_at" })}
                >
                  قدیمی ترین
                </Typography>
              </div>
            </div>

            <div className={classes.itemBox}>
              <Typography variant="subtitle2">دریافت فایل گزارش:</Typography>

              <div className={classes.methods}>
                <LazyLoadImage className={classes.Icon} src={excelIcon} onClick={getExportedData} />
              </div>
            </div>
          </Stack>

          <div className={classes.countApplicaton}>
            <Typography variant="h6">
              {`${applicationsCount.toLocaleString("fa-ir")} `}پرونده
            </Typography>

            <LazyLoadImage className={classes.countApplicationImg} src={applicationCountIcon} />
          </div>
        </div>

        <Table
          rows={tableData}
          columns={columns}
          totalPages={totalPages}
          checkboxSelection={userInfoStore?.is_superuser}
          onRowSelectionModelChange={handleSelectRow}
          loading={tableLoading}
        />
      </div>

      <ApplicationFilterModal
        open={filterModal.open}
        onClose={handleCloseFilterModal}
        reload={reload}
        setReload={setReload}
        reset={reset}
        control={control}
        watch={watch}
        resetField={resetField}
      />

      <ApplicationReferralModal
        open={referralModal.open}
        onClose={() => setReferralModal({ open: false })}
        selectedApplication={selectedRows}
        staffs={selectedStaffs}
      />
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, selectedApplication: null })}
        onConfirm={handleDeleteApplication}
        loading={deleteApplicationMutation.isPending}
        title="حذف پرونده"
        message="آیا از حذف پرونده انتخاب‌شده مطمئن هستید؟ این عملیات قابل بازگشت نیست."
      />
    </>
  );
};

export default ApplicationsList;
